import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { trackFormEvents } from '../../../../lib/analyticsEnhanced';
import { BottomSheetModal } from '../../../../components/v3/BottomSheetModal';
import {
  IOSFormGroup,
  IOSFormItem,
  IOSFormLabel,
  IOSFormToggle
} from '../ios/IOSFormGroup';

interface TaskRecurrenceEditorProps {
  isOpen: boolean;
  onClose: () => void;
  recurrenceRule?: string;
  isHabit?: boolean;
  onSave: (data: { recurrenceRule?: string; isHabit?: boolean }) => void | Promise<void>;
}

/**
 * 重复设置弹窗组件
 * 保持原有 TaskForm 中重复设置部分的完整功能
 */
export const TaskRecurrenceEditor: React.FC<TaskRecurrenceEditorProps> = ({
  isOpen,
  onClose,
  recurrenceRule,
  isHabit,
  onSave
}) => {
  const { t } = useTranslation(['v3/tasks', 'common']);

  // 本地状态
  const [localRecurrenceRule, setLocalRecurrenceRule] = useState<string | undefined>(recurrenceRule);
  const [localIsHabit, setLocalIsHabit] = useState<boolean>(isHabit || false);

  // 当弹窗打开时重置本地状态
  useEffect(() => {
    if (isOpen) {
      setLocalRecurrenceRule(recurrenceRule);
      setLocalIsHabit(isHabit || false);
    }
  }, [isOpen, recurrenceRule, isHabit]);

  // 处理重复任务开关
  const handleRecurrenceToggle = (checked: boolean) => {
    if (checked) {
      setLocalRecurrenceRule('FREQ=DAILY');
    } else {
      setLocalRecurrenceRule(undefined);
      // 取消循环时自动取消习惯
      setLocalIsHabit(false);
    }
  };

  // 处理重复频率变更
  const handleRecurrenceRuleChange = (rule: string) => {
    setLocalRecurrenceRule(rule);
  };

  // 处理习惯开关
  const handleIsHabitToggle = (checked: boolean) => {
    setLocalIsHabit(checked);
  };

  // 处理保存
  const handleSave = async () => {
    const changes = {
      recurrenceRule: localRecurrenceRule,
      isHabit: localIsHabit
    };

    // 记录编辑器保存事件
    trackFormEvents.editorSaved('recurrence_editor', changes, {
      has_recurrence: !!localRecurrenceRule,
      is_habit: localIsHabit,
      recurrence_type: localRecurrenceRule ?
        (localRecurrenceRule.includes('DAILY') ? 'daily' :
         localRecurrenceRule.includes('WEEKLY') ? 'weekly' :
         localRecurrenceRule.includes('MONTHLY') ? 'monthly' : 'custom') : 'none'
    });

    await onSave(changes);
    onClose();
  };

  return (
    <BottomSheetModal
      isOpen={isOpen}
      onClose={onClose}
      title="重复设置"
      onConfirm={handleSave}
      confirmText="保存"
      cancelText="取消"
      useGrayBackground={true}
      usePortal={true}
    >
      <div className="p-4">
        <IOSFormGroup>
          {/* 重复任务 */}
          <IOSFormItem className="flex items-center justify-between">
            <IOSFormLabel>{t('v3/tasks:form.recurrence')}</IOSFormLabel>
            <IOSFormToggle
              checked={!!localRecurrenceRule}
              onChange={handleRecurrenceToggle}
            />
          </IOSFormItem>

          {/* 循环配置区域 - 仅在开启重复时显示 */}
          {localRecurrenceRule && (
            <>
              <IOSFormItem>
                <IOSFormLabel>{t('v3/tasks:form.recurrenceFrequency')}</IOSFormLabel>
                <select
                  className="w-full px-3 py-2 bg-ios-gray-6 dark:bg-ios-dark-gray-6 rounded-ios-lg text-ios-body"
                  value={localRecurrenceRule}
                  onChange={(e) => handleRecurrenceRuleChange(e.target.value)}
                >
                  <option value="FREQ=DAILY">{t('v3/tasks:recurrence.daily', '每天')}</option>
                  <option value="FREQ=WEEKLY">{t('v3/tasks:recurrence.weekly', '每周')}</option>
                  <option value="FREQ=WEEKLY;BYDAY=MO,TU,WE,TH,FR">{t('v3/tasks:recurrence.weekdays', '工作日')}</option>
                  <option value="FREQ=MONTHLY">{t('v3/tasks:recurrence.monthly', '每月')}</option>
                </select>
              </IOSFormItem>

              {/* 习惯选项 - 在循环配置区域内 */}
              <IOSFormItem className="flex items-center justify-between">
                <div>
                  <IOSFormLabel>{t('v3/tasks:form.isHabit')}</IOSFormLabel>
                  <span className="text-ios-footnote text-ios-gray dark:text-ios-dark-gray">
                    {t('v3/tasks:form.habitDescription')}
                  </span>
                </div>
                <IOSFormToggle
                  checked={localIsHabit}
                  onChange={handleIsHabitToggle}
                />
              </IOSFormItem>
            </>
          )}
        </IOSFormGroup>
      </div>
    </BottomSheetModal>
  );
};
